import React, { FC, useState, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useUpdateMemoMutation } from '@/features/yakureki/hooks/queries/useUpdateMemoMutation'
import { useToast } from '@/shared/contexts/ToastContext'

interface NSIPSMemoPanelProps {
  yakurekiId: string
  patientName: string
  memoItems: string[]
}

export const NSIPSMemoPanel: FC<NSIPSMemoPanelProps> = ({
  yakurekiId,
  patientName,
  memoItems: initialMemoItems,
}) => {
  const { showToast } = useToast()
  const [memoItems, setMemoItems] = useState<string[]>(initialMemoItems)
  const [newItem, setNewItem] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const updateMemoMutation = useUpdateMemoMutation()

  // DB保存
  const saveMemo = useCallback(
    (items: string[]) => {
      updateMemoMutation.mutate({ yakurekiId, memoItems: items })
    },
    [yakurekiId, updateMemoMutation]
  )

  const handleAddItem = useCallback(() => {
    if (newItem.trim()) {
      const updated = [...memoItems, newItem.trim()]
      setMemoItems(updated)
      setNewItem('')
      saveMemo(updated)
    }
  }, [newItem, memoItems, saveMemo])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
        e.preventDefault()
        handleAddItem()
      }
    },
    [handleAddItem]
  )

  const handleDeleteItem = useCallback(
    (index: number) => {
      const updated = memoItems.filter((_, i) => i !== index)
      setMemoItems(updated)
      saveMemo(updated)
    },
    [memoItems, saveMemo]
  )

  const handleCopy = useCallback(() => {
    const text = memoItems.map((item) => `・${item}`).join('\n')
    navigator.clipboard.writeText(text)
    showToast('メモをコピーしました', 'success', 2000)
  }, [memoItems, showToast])

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* CollapsibleCardと同じ高さのヘッダー行 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1,
          px: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: colorTokens.grey[700],
          }}
        >
          メモ — {patientName}
        </Typography>
        {memoItems.length > 0 && (
          <Tooltip title="コピー">
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* 白いカード部分（CollapsibleCardの白Box構造と同一） */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colorTokens.background.paper,
          borderRadius: 2,
          border: `1px solid ${colorTokens.border.light}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* メモ一覧 */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
          {memoItems.length > 0 ? (
            <List dense disablePadding>
              {memoItems.map((item, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  sx={{
                    py: 0.5,
                    borderBottom: `1px solid ${colorTokens.border.light}`,
                  }}
                >
                  <ListItemText primary={`・${item}`} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDeleteItem(index)}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              sx={{
                color: colorTokens.text.secondary,
                py: 2,
                textAlign: 'center',
              }}
            >
              Enterでメモを追加
            </Typography>
          )}
        </Box>

        {/* 入力欄 */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${colorTokens.border.light}`,
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder="メモを入力... (Enterで追加)"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </Box>
      </Box>
    </Box>
  )
}
